import Fuse from "fuse.js";

/**
 * Appends a template token after existing text, adding a separating space only when the existing
 * text doesn't already end in whitespace or an underscore — both read as "already separated" when
 * a customer is building a template token-by-token (Field Mapping's Formatting mode and File
 * Rename's templates both use this for their `TokenButton` click-to-append behavior). Addison:
 * clicking a token shouldn't double up a space the customer already typed, and a trailing
 * underscore should count the same as a space for this purpose.
 */
export const appendTemplateToken = (current: string, token: string): string => {
  if (!current) {
    return token;
  }
  return /[\s_]$/.test(current) ? `${current}${token}` : `${current} ${token}`;
};

/**
 * While the customer is mid-way through typing a `{token}` by hand (an unclosed `{` with no `}`
 * after it yet) rather than clicking a chip, returns the partial name typed so far — Field
 * Mapping's and File Rename's token chip rows both live-filter down to chips matching it, the same
 * way `Select`'s own `isSearchable` narrows its options as you type. Returns `null` when there's
 * no open, unclosed `{` (nothing to filter by — every chip shows), matching an `Input`'s plain
 * string `value` since neither app tracks real cursor position — this reads from the LAST `{` in
 * the string, not literally "wherever the caret is", which matches the common case (typing at the
 * end) and degrades gracefully (shows everything) once a `}` closes it.
 */
export const openTemplateTokenQuery = (template: string): string | null => {
  const lastOpen = template.lastIndexOf("{");
  if (lastOpen === -1) {
    return null;
  }
  const afterOpen = template.slice(lastOpen + 1);
  return afterOpen.includes("}") ? null : afterOpen;
};

/**
 * Inserts a clicked chip's token — if the customer is mid-way through hand-typing a `{token}`
 * (an open, unclosed `{`, same condition `openTemplateTokenQuery` checks), the clicked chip
 * REPLACES that in-progress `{partial` text rather than being appended after it (so clicking the
 * chip a live-filtered search surfaced completes what's being typed, instead of leaving the typed
 * fragment behind and appending a second, redundant token at the end). Otherwise falls back to
 * `appendTemplateToken`'s plain "append after existing text" behavior.
 */
export const insertTemplateToken = (current: string, token: string): string => {
  const lastOpen = current.lastIndexOf("{");
  if (lastOpen !== -1 && !current.slice(lastOpen + 1).includes("}")) {
    return current.slice(0, lastOpen) + token;
  }
  return appendTemplateToken(current, token);
};

/**
 * The word the customer is actively typing at the END of a plain-text string — the trailing run
 * of word characters (letters/digits/underscore), stopping at whitespace or any other character.
 * Field Mapping's Conditional `when` field (a Lucene-style `Field:Value AND ...` string, see
 * `spotterConditionEval.ts`) has no `{token}` bracket syntax to key off of like
 * `openTemplateTokenQuery` — this is what live-filters its field chips instead, treating `:`,
 * `(`, `)`, etc. as word boundaries the same as whitespace since those are the condition
 * language's own operator characters. Returns `""` when the string is empty or already ends in a
 * boundary (nothing being typed).
 */
export const currentWordQuery = (text: string): string => {
  const match = text.match(/[A-Za-z0-9_]*$/);
  return match ? match[0] : "";
};

const CONDITION_CONNECTOR_RE = /\b(AND|OR|NOT)$/i;
const IS_CONDITION_CONNECTOR_RE = /^(AND|OR|NOT)$/i;

/**
 * The field NAME being actively typed at the end of a Conditional `when` string, or `""` when
 * nothing is — like `currentWordQuery`, but additionally treats a trailing word as NOT an
 * in-progress field name when it's either immediately preceded by `:` (a just-finished VALUE:
 * once `Field:Rush` is typed, "Rush" shouldn't still live-filter/replace against the FIELD name
 * list by "Rush") or is itself a completed boolean keyword (`AND`/`OR`/`NOT`).
 */
export const currentFieldNameQuery = (text: string): string => {
  const word = currentWordQuery(text);
  if (!word || IS_CONDITION_CONNECTOR_RE.test(word)) {
    return "";
  }
  return text[text.length - word.length - 1] === ":" ? "" : word;
};

/**
 * Inserts a clicked field chip into a Conditional `when` string. While the customer is actively
 * typing a field name (`currentFieldNameQuery` is non-empty), the chip REPLACES that in-progress
 * text — the plain-text equivalent of `insertTemplateToken`'s `{partial}` replacement. Otherwise —
 * nothing being typed, or the trailing word is really a just-finished VALUE after a `:` — the chip
 * starts a new clause: appended directly when `current` already ends with `(`/`AND`/`OR`/`NOT` (or
 * is empty), else preceded by an inserted `" AND "` so two clauses are never left adjacent with no
 * boolean connector between them (ambiguous/invalid Lucene syntax) just because the customer
 * clicked a chip instead of typing.
 */
export const insertConditionField = (
  current: string,
  fieldToken: string
): string => {
  const query = currentFieldNameQuery(current);
  if (query) {
    return current.slice(0, current.length - query.length) + fieldToken;
  }
  const trimmed = current.trimEnd();
  if (trimmed === "" || trimmed.endsWith("(")) {
    return `${trimmed}${fieldToken}`;
  }
  if (CONDITION_CONNECTOR_RE.test(trimmed)) {
    return `${trimmed} ${fieldToken}`;
  }
  return `${trimmed} AND ${fieldToken}`;
};

/**
 * Fuzzy-filters a list of bare token names (braces stripped) down to the ones matching an
 * in-progress `{`-typed query from `openTemplateTokenQuery` — same `Fuse` fuzzy-match approach
 * `CandidateAssignmentsScreen.tsx`'s own `fuseFilter` already uses for its candidate-name search,
 * reused here rather than inventing a second filtering approach, but with a tighter `threshold`
 * (`0.2`, vs. that screen's `0.4`) — this is a small, exact-named field list a customer is
 * actively typing a real token name into, not an open-ended people-search, so a near-exact match
 * reads as more correct than a loose one. Returns every name, unfiltered/in original order, when
 * `query` is `null` or empty (nothing being typed yet).
 */
export const filterTemplateTokenNames = (
  names: string[],
  query: string | null
): string[] => {
  if (query === null || query.trim().length === 0) {
    return names;
  }
  return new Fuse(names, { threshold: 0.2 }).search(query).map((r) => r.item);
};
